import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import EditIcon from "@mui/icons-material/Edit";

import DataTable from '../../../components/common/datatable/DataTable'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import ButtonComponent from '../../../components/common/button/Button';

const DataDetails = ({ row, pagination, paginationPageSize, paginationPageSizeSelector, onSelectionChanged, rowSelection, handleCellValueChange, MfgLTSim, SupplierTTSim, IQDSIM, DWSim, GRPSim, resetTrigger }) => {
  const [isGridReady, setIsGridReady] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState("");
  const [disableFilterBtn, setDisableFilterBtn] = useState(true);
  const [columnState, setColumnState] = useState(null);

  const parseSimNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // Helper function to calculate theoreticalTotalPDTSim
  const calculateTheoreticalTotalPDTSim = (data) => {
    if (!data) return 0;
    
    const compute = (simField, baseField) => {
      const simRaw = data[simField];
      const baseRaw = data[baseField];
      const base = parseSimNumber(baseRaw);
      if (simRaw === null || simRaw === undefined) return base;
      const simNum = parseFloat(simRaw);
      if (!Number.isNaN(simNum) && simNum < 0) return base + simNum;
      return parseSimNumber(simRaw);
    };

    const mfg = compute("manufacturingLtSim", "manufacturingLt");
    const supplier = compute("supplierTransitTimeSim", "supplierTransitTime");
    const iqd = compute("iqdSim", "iqd");
    const dock = compute("dockwaitingSim", "dockWaiting");
    const gr = compute("grProcessingSim", "grProcessing");
    const tm = compute("tmTransitTimeSim", "tmTransitTime");
    return mfg + supplier + iqd + dock + gr + tm;
  };

  // Helper function to calculate Inventory Impact Percentage
  const calculateInventoryImpactPercent = (data) => {
    if (!data) return "0%";

    const theoreticalTotalPDT = parseSimNumber(data.theoreticalTotalPDT);
    const theoreticalTotalPDTSim = calculateTheoreticalTotalPDTSim(data);

    if (theoreticalTotalPDT <= 0) {
      return "0%";
    }

    const ratioArg = theoreticalTotalPDTSim / theoreticalTotalPDT;
    if (ratioArg <= 0 || Number.isNaN(ratioArg)) {
      return "0%";
    }

    // Calculate Ratio = SQRT(theoreticalTotalPDTSim / theoreticalTotalPDT)
    const ratio = Math.sqrt(ratioArg);

    // Calculate Impact percentage = ROUND((Ratio-1)*100, 2)
    const impactPercent = Math.round((ratio - 1) * 100 * 100) / 100;
    return impactPercent + "%";
  };

  // Helper function to calculate Inventory Impact Value
  const calculateInventoryImpactValue = (data) => {
    if (!data) return 0;

    const theoreticalTotalPDT = parseSimNumber(data.theoreticalTotalPDT);
    const theoreticalTotalPDTSim = calculateTheoreticalTotalPDTSim(data);
    const safetyStockQty = parseSimNumber(data.safetyStockQty);
    const contractNetPrice = parseSimNumber(data.contractNetPrice);

    if (theoreticalTotalPDT <= 0) {
      return 0;
    }

    const ratioArg = theoreticalTotalPDTSim / theoreticalTotalPDT;
    if (ratioArg <= 0 || Number.isNaN(ratioArg)) {
      return 0;
    }

    // Calculate Ratio = SQRT(theoreticalTotalPDTSim / theoreticalTotalPDT)
    const ratio = Math.sqrt(ratioArg);

    // Calculate Impact Value = (Ratio-1) * BaseLineValue
    const baseLineValue = safetyStockQty * contractNetPrice;
    const impactValue = (ratio - 1) * baseLineValue;
    return Math.round(impactValue * 100) / 100;
  };

  const columnData = useMemo(() => {
      return [
          {
            headerName: "Material",
            field: "materialId",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Material Description",
            field: "materialDescription",
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Plant",
            field: "plant",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Region",
            field: "region",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Supplier",
            field: "supplierId",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Supplier Description",
            field: "supplierDescription",
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Auto Co-Ship",
            field: "autoCoShipStatus",
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Incoterms",
            field: "incoterm",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Ship from",
            field: "shipFrom",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Ship to",
            field: "shipTo",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Mfg Lead time (PM)",
            field: "manufacturingLt",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Mfg Lead time (Sim)",
            colId: "manufacturingLtSim",
            field: "manufacturingLtSim",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
            cellEditor: "agNumberCellEditor",
            editable: true,
            cellClass: "cpsComments-cell",
            valueParser: (params) => {
              const parsed = parseFloat(params.newValue);
              return Number.isNaN(parsed) ? params.newValue : parsed;
            },
            valueGetter: (params) => {
              if (!params.data) return "";
              return params.data.manufacturingLtSim ?? params.data.manufacturingLt;
            },
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, manufacturingLtSim: parseSimNumber(params.newValue) };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  <EditIcon fontSize="sm" />
                </div>
              );
            },
          },
          {
            headerName: "Supplier Transit time (PM)",
            field: "supplierTransitTime",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Supplier Transit time (Sim)",
            colId: "supplierTransitTimeSim",
            field: "supplierTransitTimeSim",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
            cellEditor: "agNumberCellEditor",
            editable: true,
            cellClass: "cpsComments-cell",
            valueParser: (params) => {
              const parsed = parseFloat(params.newValue);
              return Number.isNaN(parsed) ? params.newValue : parsed;
            },
            valueGetter: (params) => {
              if (!params.data) return "";
              return params.data.supplierTransitTimeSim ?? params.data.supplierTransitTime;
            },
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, supplierTransitTimeSim: parseSimNumber(params.newValue) };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  <EditIcon fontSize="sm" />
                </div>
              );
            },
          },
          {
            headerName: "IQD (PM)",
            field: "iqd",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "IQD (Sim)",
            colId: "iqdSim",
            field: "iqdSim",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
            cellEditor: "agNumberCellEditor",
            editable: true,
            cellClass: "cpsComments-cell",
            valueParser: (params) => {
              const parsed = parseFloat(params.newValue);
              return Number.isNaN(parsed) ? params.newValue : parsed;
            },
            valueGetter: (params) => {
              if (!params.data) return "";
              return params.data.iqdSim ?? params.data.iqd;
            },
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, iqdSim: parseSimNumber(params.newValue) };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  <EditIcon fontSize="sm" />
                </div>
              );
            },
          },
          {
            headerName: "Dockwaiting",
            field: "dockWaiting",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Dockwaiting (Sim)",
            colId: "dockwaitingSim",
            field: "dockwaitingSim",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
            cellEditor: "agNumberCellEditor",
            editable: true,
            cellClass: "cpsComments-cell",
            valueParser: (params) => {
              const parsed = parseFloat(params.newValue);
              return Number.isNaN(parsed) ? params.newValue : parsed;
            },
            valueGetter: (params) => {
              if (!params.data) return "";
              return params.data.dockwaitingSim ?? params.data.dockWaiting;
            },
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, dockwaitingSim: parseSimNumber(params.newValue) };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  <EditIcon fontSize="sm" />
                </div>
              );
            },
          },
          {
            headerName: "GR processing",
            field: "grProcessing",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "GR processing (Sim)",
            colId: "grProcessingSim",
            field: "grProcessingSim",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
            cellEditor: "agNumberCellEditor",
            editable: true,
            cellClass: "cpsComments-cell",
            valueParser: (params) => {
              const parsed = parseFloat(params.newValue);
              return Number.isNaN(parsed) ? params.newValue : parsed;
            },
            valueGetter: (params) => {
              if (!params.data) return "";
              return params.data.grProcessingSim ?? params.data.grProcessing;
            },
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, grProcessingSim: parseSimNumber(params.newValue) };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  <EditIcon fontSize="sm" />
                </div>
              );
            },
          },
          {
            headerName: "TM transit time",
            field: "tmTransitTime",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "TM transit time (Sim)",
            colId: "tmTransitTimeSim",
            field: "tmTransitTimeSim",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
            cellEditor: "agNumberCellEditor",
            editable: true,
            cellClass: "cpsComments-cell",
            valueParser: (params) => {
              const parsed = parseFloat(params.newValue);
              return Number.isNaN(parsed) ? params.newValue : parsed;
            },
            valueGetter: (params) => {
              if (!params.data) return "";
              return params.data.tmTransitTimeSim ?? params.data.tmTransitTime;
            },
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, tmTransitTimeSim: parseSimNumber(params.newValue) };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  <EditIcon fontSize="sm" />
                </div>
              );
            },
          },
          {
            headerName: "Total PDT",
            field: "theoreticalTotalPDT",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Total PDT (SIM)",
            colId: "theoreticalTotalPDTSim",
            field: "theoreticalTotalPDT",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
            valueGetter: (params) => {
              if (!params.data) return "";
              // compute: if sim value is undefined -> use base
              // if sim value is negative -> treat as delta and do base + sim
              // otherwise use sim value (replacement)
              const compute = (simField, baseField) => {
                const simRaw = params.data[simField];
                const baseRaw = params.data[baseField];
                const base = parseSimNumber(baseRaw);
                if (simRaw === null || simRaw === undefined) return base;
                const simNum = parseFloat(simRaw);
                if (!Number.isNaN(simNum) && simNum < 0) return base + simNum;
                return parseSimNumber(simRaw);
              };

              const mfg = compute("manufacturingLtSim", "manufacturingLt");
              const supplier = compute("supplierTransitTimeSim", "supplierTransitTime");
              const iqd = compute("iqdSim", "iqd");
              const dock = compute("dockwaitingSim", "dockWaiting");
              const gr = compute("grProcessingSim", "grProcessing");
              const tm = compute("tmTransitTimeSim", "tmTransitTime");
              return mfg + supplier + iqd + dock + gr + tm;
            },
          },
          {
            headerName: "Total PDT (Contract)",
            field: "contractTotalPDT",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
          },
          {
            headerName: "Inventory Impact %",
            colId: "inventoryImpactPercentSim",
            field: "inventoryImpactPercent",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
            valueGetter: (params) => {
              if (!params.data) return "0%";
              return calculateInventoryImpactPercent(params.data);
            },
          },
          {
            headerName: "Inventory Impact Value",
            colId: "inventoryImpactValueSim",
            field: "inventoryImpactValue",
            width: 200,
            sortable: true,
            resizable: true,
            unSortIcon: true,
            valueGetter: (params) => {
              if (!params.data) return 0;
              return calculateInventoryImpactValue(params.data);
            },
            cellRenderer: (params) => {
              return `$${params.value}`;
            },
          },
        ];
    }, []);

  const selectionColumnDef = useMemo(
    () => ({
      pinned: "left",
    }),
    [],
  );

  const gridApiRef = useRef(null);

  const onColumnChange = (e) => {
    const col = e.target.value;
    setSelectedColumn(col);
    if (col && gridApiRef.current) {
      gridApiRef.current.ensureColumnVisible(col);
    }
  };

  const onGridReady = (params) => {
    gridApiRef.current = params.api; // Store the grid API instance locally
    setIsGridReady(true);
    if (columnState) {
      params.columnApi.applyColumnState({
        state: columnState,
        applyOrder: true,
      });
    }
  };

  useEffect(() => {
    if (resetTrigger > 0) {
      setSelectedColumn("");
    }
  }, [resetTrigger]);

  useEffect(() => {
    // Restore filter model from localStorage on component mount
    if (isGridReady && gridApiRef?.current) {
      const savedFilterModel = localStorage.getItem(
        "inventorySimulateFilterModel",
      );
      if (savedFilterModel) {
        gridApiRef.current.setFilterModel(JSON.parse(savedFilterModel));
        setDisableFilterBtn(true); // Enable the "Clear All Filters" button
      }
    }
  }, [isGridReady]);

  const clearAllFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null); // Clear all filters
      localStorage.removeItem("inventorySimulateFilterModel");
      setDisableFilterBtn(true);
    }
  };

  const onFilterChanged = () => {
    if (gridApiRef.current) {
      const filterModel = gridApiRef.current.getFilterModel();
      setDisableFilterBtn(Object.keys(filterModel).length === 0); // Enable button if filters exist
      localStorage.setItem(
        "inventorySimulateFilterModel",
        JSON.stringify(filterModel),
      );
    }
  };

  const onColumnMoved = useCallback((params) => {
    if (
      params &&
      params.columnApi &&
      typeof params.columnApi.getColumnState === "function"
    ) {
      setColumnState(params.columnApi.getColumnState());
    }
  }, []);

  const gridOptions = {
    getRowId: (params) => {
      return `${params?.data?.id}`;
    },
    overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
    overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
    defaultColDef: {
      sortable: true,
      filter: "agMultiColumnFilter",
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.5,
        display: "flex",
        alignItems: "center",
      },
      valueGetter: (params) => {
          if (!params.data) return "";

          const field = params.colDef.field;
          const value = params.data[field];

          return value === null || value === undefined || value === "" ? "" : value;
      },
    },
    onColumnMoved,
    onFilterChanged,
  };

  const handleExportToExcel = () => {
    if (!gridApiRef.current) return;

    // Get all displayed columns currently in the grid
    const allColumns = gridApiRef.current.getAllDisplayedColumns();

    // Filter out the columns you want to exclude
    const columnsForExport = allColumns
      .filter(col => col.getColId() !== 'statusIcon')
      .map(col => col.getColId()); // Get just the IDs

    // Call the export API with the specific column keys
    gridApiRef.current.exportDataAsExcel({
      columnKeys: columnsForExport,
    });
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          marginBottom: "20px",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
            <InputLabel>Jump to Column</InputLabel>
            <Select
              id="col-select"
              value={selectedColumn}
              label="Jump to Column"
              onChange={onColumnChange}
            >
              <MenuItem value="">Select Column</MenuItem>
              {columnData?.map((col) => {
                const id = col?.colId || col?.field;
                return (
                  <MenuItem key={id} value={id}>
                    {col?.headerName || col?.field}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <ButtonComponent
            label="Export to Excel"
            bgColor="black"
            color="white"
            onClick={handleExportToExcel}
          />
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <ButtonComponent
            maxWidth="150px"
            label="Clear All Filters"
            bgColor="black"
            color="white"
            disabled={disableFilterBtn}
            onClick={clearAllFilters}
          />
        </div>
      </div>
      <DataTable
        columns={columnData}
        rows={row || []}
        paginationProp={pagination}
        paginationPageSize={paginationPageSize}
        paginationPageSizeSelector={paginationPageSizeSelector}
        selectionColumnDef={selectionColumnDef}
        rowSelection={rowSelection}
        onSelectionChanged={onSelectionChanged}
        gridOptions={gridOptions}
        onCellValueChanged={handleCellValueChange}
        onGridReady={onGridReady}
      />
    </>
  )
}

export default DataDetails